import { intervalToDuration } from 'date-fns'

const DAY_MS = 86_400_000

export function plural(n: number, one: string, many?: string): string {
  return Math.abs(n) === 1 ? one : (many ?? `${one}s`)
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(n)))
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function coarse(from: Date, to: Date): string {
  const d = intervalToDuration({ start: from, end: to })
  const parts: string[] = []
  if (d.years) parts.push(`${d.years} ${plural(d.years, 'year')}`)
  if (d.months) parts.push(`${d.months} ${plural(d.months, 'month')}`)
  if (parts.length < 2 && d.days) parts.push(`${d.days} ${plural(d.days, 'day')}`)
  return parts.join(' ')
}

/** Ticking countdown to a future date. */
export function formatCountdown(target: Date, now: Date): string {
  const ms = target.getTime() - now.getTime()
  if (ms <= 0) return '00:00:00'
  const days = Math.floor(ms / DAY_MS)
  if (days < 60) {
    const rest = ms - days * DAY_MS
    const clock = `${pad(Math.floor(rest / 3_600_000))}:${pad(Math.floor((rest % 3_600_000) / 60_000))}:${pad(
      Math.floor((rest % 60_000) / 1000),
    )}`
    return days > 0 ? `${days} ${plural(days, 'day')} · ${clock}` : clock
  }
  return coarse(now, target) || `${days} ${plural(days, 'day')}`
}

export interface Distance {
  main: string
  alt: string
}

/**
 * Distance to a unit expressed in several units at once: days always, plus
 * weeks and months when they add anything. Far dates lead with years/months
 * and keep the raw day count as the secondary line.
 */
export function distanceParts(target: Date, now: Date): Distance {
  const ms = target.getTime() - now.getTime()
  const past = ms < 0
  const days = Math.floor(Math.abs(ms) / DAY_MS)
  if (days === 0) return { main: past ? 'happening now' : 'today', alt: '' }

  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30.436875)
  const wrap = (value: string) => (past ? `${value} ago` : `in ${value}`)

  if (days < 400) {
    const alt: string[] = []
    if (weeks >= 1) alt.push(`${weeks} ${plural(weeks, 'week')}`)
    if (months > 1) alt.push(`${months} ${plural(months, 'month')}`)
    return { main: wrap(`${days} ${plural(days, 'day')}`), alt: alt.join(' · ') }
  }

  const coarseValue = coarse(past ? target : now, past ? now : target)
  return {
    main: wrap(coarseValue || `${days} ${plural(days, 'day')}`),
    alt: `${formatNumber(days)} ${plural(days, 'day')} · ${formatNumber(weeks)} ${plural(weeks, 'week')}`,
  }
}

/** How long ago a past moment started. */
export function formatElapsed(from: Date, now: Date): string {
  const ms = now.getTime() - from.getTime()
  if (ms < 0) return '—'
  if (ms < DAY_MS) return 'happening now'
  const days = Math.floor(ms / DAY_MS)
  if (days < 60) return `${days} ${plural(days, 'day')} ago`
  const value = coarse(from, now)
  return value ? `${value} ago` : 'a while ago'
}
